"use client";

/**
 * Public privacy statement body (/privacy) — bilingual.
 *
 * Rendered inside {@link EnglishMarketingScope}, so it DEFAULTS to English and
 * the EN/NL toggle in {@link LandingNav} flips the locale. The full bilingual
 * copy lives locally in {@link COPY} (not the shared i18n dictionary): a legal
 * statement is easiest to keep faithful when its Dutch source and the English
 * translation sit side by side in one file.
 *
 * The statutory company facts come from {@link COMPANY} (KvK/BTW/address/inbox)
 * and render identically in both languages — they are Dutch statutory facts
 * regardless of UI language.
 */

import Link from "next/link";
import { LandingNav } from "@/components/landing-nav";
import { COMPANY } from "@/lib/company";
import { useLocale } from "@/i18n/provider";

const LAST_UPDATED = "2026-07-08";

type SectionCopy = {
  title: string;
  paras?: string[];
  list?: string[];
};

const COPY = {
  nl: {
    eyebrow: "Privacy",
    title: "Privacyverklaring",
    intro:
      "Deze privacyverklaring legt uit hoe FixControl persoonsgegevens verwerkt via de publieke website en de FixControl-dienst.",
    lastUpdated: "Laatst bijgewerkt",
    s1: {
      title: "Wie is verantwoordelijk?",
      p1: `FixControl is een handelsnaam van ${COMPANY.legalName}. Voor de publieke website is ${COMPANY.legalName} de verwerkingsverantwoordelijke.`,
      p2: `Voor gegevens die klanten in de FixControl-dienst verwerken, kan FixControl optreden als verwerker namens de klant, afhankelijk van de gemaakte afspraken. In sommige situaties kan ${COMPANY.legalName} juist zelf verwerkingsverantwoordelijke zijn. Welke rol van toepassing is, hangt af van de context en wordt waar nodig vastgelegd in een verwerkersovereenkomst of klantcontract.`,
    },
    s2: {
      title: "Welke persoonsgegevens verwerken wij?",
      intro:
        "Afhankelijk van hoe u FixControl gebruikt en welke integraties zijn ingeschakeld, kunnen wij de volgende categorieën gegevens verwerken. Niet elke klant gebruikt alle onderdelen, dus niet al deze gegevens zijn altijd van toepassing.",
      cats: [
        {
          h: "Contact- en bedrijfsgegevens",
          p: "Naam, e-mailadres, organisatie, functie en de inhoud van uw bericht.",
        },
        {
          h: "Account- en gebruikersgegevens",
          p: "Naam, e-mailadres, rol en rechten, en koppelingen met tenant, project of workspace.",
        },
        {
          h: "Support-, issue- en workflowgegevens",
          p: "Tickets, issues, berichten via Slack, Freshdesk, Jira, Linear, GitHub of GitLab (afhankelijk van de gebruikte integraties), goedkeuringen, audit events en workflowstatussen.",
        },
        {
          h: "Technische en beveiligingsgegevens",
          p: "IP-adres, browser- en user-agentgegevens, logbestanden, foutmeldingen en audit- en security-events.",
        },
        {
          h: "Websitegegevens",
          p: "Bezochte pagina’s en referrer. Globale, privacyvriendelijke analytics kan worden gebruikt als die later wordt ingeschakeld.",
        },
        {
          h: "Integratiegegevens",
          p: "Metadata en inhoud die nodig is om gekoppelde systemen te laten werken. Tokens en secrets worden beveiligd verwerkt en worden niet publiek getoond.",
        },
        {
          h: "AI-gerelateerde gegevens",
          p: "Prompts, context en outputs die nodig zijn voor de uitvoering van workflows, alleen voor zover klanten of gebruikers deze binnen de dienst aanbieden.",
        },
      ],
      outro:
        "Afhankelijk van de klantconfiguratie kunnen wij ook codebase- of repositorymetadata verwerken, voor zover dat nodig is om de dienst te leveren.",
    },
    s3: {
      title: "Waarom verwerken wij gegevens?",
      list: [
        "het beantwoorden van contactverzoeken;",
        "het leveren van de FixControl-dienst;",
        "het aanmaken en beheren van accounts, tenants en projecten;",
        "het verwerken van support- en engineering-workflows;",
        "het tonen en uitvoeren van goedkeuringen;",
        "het uitvoeren van integraties met klanttools;",
        "beveiliging, audit logging en fraudepreventie;",
        "het verbeteren van de website en de productervaring;",
        "het voldoen aan wettelijke verplichtingen.",
      ],
    },
    s4: {
      title: "Grondslagen",
      intro: "Wij verwerken persoonsgegevens op basis van de volgende AVG-grondslagen:",
      list: [
        "de uitvoering van een overeenkomst;",
        "een gerechtvaardigd belang, bijvoorbeeld beveiliging, audit logging en productverbetering;",
        "het voldoen aan een wettelijke verplichting;",
        "uw toestemming waar dat nodig is, bijvoorbeeld voor niet-noodzakelijke cookies of bepaalde marketingcommunicatie;",
        "precontractuele communicatie bij een demo- of contactaanvraag.",
      ],
    },
    s5: {
      title: "AI-verwerking",
      paras: [
        "FixControl gebruikt AI-functies om workflows te ondersteunen, bijvoorbeeld voor triage, samenvattingen, planvoorstellen, conceptantwoorden of remediation-voorstellen. Menselijke goedkeuring blijft onderdeel van risicovolle acties.",
        "Welke AI-diensten of modellen worden gebruikt, kan afhankelijk zijn van de klantconfiguratie. Klantdata wordt alleen gebruikt om de dienst te leveren en wordt niet verkocht.",
        "Afhankelijk van de configuratie kunnen AI-dienstverleners als verwerker worden ingezet. In klantafspraken kunnen aanvullende voorwaarden worden vastgelegd over welke AI-diensten worden gebruikt en welke gegevens daarmee worden gedeeld.",
      ],
    },
    s6: {
      title: "Integraties en derden",
      intro:
        "Om de dienst te leveren kunnen wij gebruikmaken van verwerkers en andere derden. Welke partijen dat zijn, hangt af van de klantconfiguratie. Categorieën zijn onder andere:",
      list: [
        "hosting- en infrastructuurproviders;",
        "e-mail- en communicatieproviders;",
        "een analyticsprovider, indien ingeschakeld;",
        "AI-dienstverleners;",
        "integratieplatforms zoals Slack, Microsoft Teams/Office 365, Google (Gmail), Freshdesk, Jira, Linear, GitHub, GitLab en Kubernetes/Argo, afhankelijk van de klantconfiguratie;",
        "betalings- of administratieproviders, indien later relevant.",
      ],
      subIntro:
        "Onafhankelijk van de klantconfiguratie maken wij voor de kernfunctionaliteit van de dienst standaard gebruik van de volgende subverwerkers:",
      subList: [
        "TransIP B.V. (hosting, Nederland) — host de FixControl-dienst en de opgeslagen gegevens op infrastructuur in de Europese Unie;",
        "Anthropic, PBC (AI-taalmodellen) — verwerkt prompts, context en outputs voor zover nodig om de AI-functies van de dienst te leveren;",
        "Resend, Inc. (transactionele e-mail) — verzorgt e-mail vanuit de dienst, zoals meldingen en goedkeuringsverzoeken.",
      ],
      subOutro:
        "Wij kunnen deze lijst van subverwerkers van tijd tot tijd bijwerken. De actuele lijst staat altijd in deze privacyverklaring; bij belangrijke wijzigingen kunnen wij klanten daarover actief informeren.",
      outro:
        "Wij delen gegevens alleen voor zover nodig om de dienst te leveren, te beveiligen, wettelijke verplichtingen na te komen of op basis van klantconfiguratie. Specifieke verwerkers en afspraken kunnen worden vastgelegd in een verwerkersovereenkomst (DPA) of in het klantcontract.",
    },
    s7: {
      title: "Internationale doorgifte",
      paras: [
        "De FixControl-dienst zelf wordt gehost op infrastructuur in de Europese Unie (Nederland).",
        "Sommige verwerkers, waaronder de hierboven genoemde AI- en e-maildienstverleners, kunnen buiten de Europese Economische Ruimte (EER) gevestigd zijn. Waar dat het geval is en nodig is, worden passende waarborgen gebruikt, zoals standaardcontractbepalingen of vergelijkbare mechanismen.",
      ],
    },
    s8: {
      title: "Bewaartermijnen",
      intro:
        "Wij bewaren gegevens niet langer dan nodig. De bewaartermijn hangt af van het soort gegevens en het doel:",
      list: [
        "contactverzoeken: zolang nodig voor opvolging en de zakelijke administratie;",
        "accountgegevens: zolang het account actief is of zolang nodig voor wettelijke of contractuele verplichtingen;",
        "audit- en security-logs: zolang nodig voor beveiliging, compliance en geschilafhandeling;",
        "klantdata: volgens het klantcontract, de configuratie of de verwerkersovereenkomst;",
        "website-analytics: geaggregeerd en zo kort als praktisch passend.",
      ],
    },
    s9: {
      title: "Beveiliging",
      intro:
        "Wij nemen passende technische en organisatorische maatregelen om persoonsgegevens te beschermen, waaronder:",
      list: [
        "toegangscontrole en rollen;",
        "scope per tenant en project;",
        "logging en audit trails;",
        "versleuteling waar passend;",
        "beveiligd beheer van secrets;",
        "menselijke goedkeuringen voor risicovolle workflows;",
        "monitoring en beveiligingsmaatregelen.",
      ],
    },
    s10: {
      title: "Cookies en analytics",
      intro:
        "FixControl gebruikt privacyvriendelijke, geaggregeerde website-analyse om te begrijpen welke publieke pagina’s worden bezocht. We gebruiken geen session replay of advertentietracking op de publieke website tenzij dit expliciet wordt vermeld.",
      list: [
        "noodzakelijke en functionele cookies kunnen worden gebruikt om de website en de dienst te laten werken;",
        "beperkte, privacyvriendelijke analytics kan worden gebruikt om de prestaties van de publieke website te meten;",
        "trackingcookies, advertentiepixels of session replay worden niet gebruikt, tenzij dit expliciet wordt vermeld en waar nodig toestemming wordt gevraagd;",
        "u kunt cookies beheren via de instellingen van uw browser.",
      ],
    },
    s11: {
      title: "Uw rechten",
      intro: "U heeft op grond van de AVG onder meer de volgende rechten:",
      list: [
        "recht op inzage;",
        "recht op rectificatie;",
        "recht op verwijdering;",
        "recht op beperking van de verwerking;",
        "recht van bezwaar;",
        "recht op overdraagbaarheid van gegevens;",
        "recht om gegeven toestemming in te trekken;",
        "recht om een klacht in te dienen bij de Autoriteit Persoonsgegevens.",
      ],
      contactBefore: "Wilt u een van deze rechten uitoefenen? Neem dan contact op via ",
      contactAfter: ".",
    },
    s12: {
      title: "Contact",
      intro:
        "Heeft u vragen over deze privacyverklaring of over de verwerking van uw gegevens? Neem dan contact met ons op.",
      tradeNameLine: `een handelsnaam van ${COMPANY.legalName}`,
      kvkLabel: "KvK",
      vatLabel: "BTW-id",
      emailLabel: "E-mail",
    },
    s13: {
      title: "Wijzigingen",
      paras: [
        "Wij kunnen deze privacyverklaring van tijd tot tijd aanpassen. De meest recente versie staat altijd op deze pagina. Bij belangrijke wijzigingen kunnen wij u daarover actief informeren.",
      ],
    },
    contactLink: "Neem contact op",
  },
  en: {
    eyebrow: "Privacy",
    title: "Privacy Policy",
    intro:
      "This privacy statement explains how FixControl processes personal data through the public website and the FixControl service.",
    lastUpdated: "Last updated",
    s1: {
      title: "Who is responsible?",
      p1: `FixControl is a trade name of ${COMPANY.legalName}. For the public website, ${COMPANY.legalName} is the data controller.`,
      p2: `For data that customers process within the FixControl service, FixControl may act as a processor on behalf of the customer, depending on the agreements made. In some situations ${COMPANY.legalName} may itself be the data controller. Which role applies depends on the context and is, where necessary, recorded in a data processing agreement (DPA) or customer contract.`,
    },
    s2: {
      title: "Which personal data do we process?",
      intro:
        "Depending on how you use FixControl and which integrations are enabled, we may process the following categories of data. Not every customer uses all components, so not all of this data is always applicable.",
      cats: [
        {
          h: "Contact and company data",
          p: "Name, email address, organisation, job title and the content of your message.",
        },
        {
          h: "Account and user data",
          p: "Name, email address, role and permissions, and links to a tenant, project or workspace.",
        },
        {
          h: "Support, issue and workflow data",
          p: "Tickets, issues, messages via Slack, Freshdesk, Jira, Linear, GitHub or GitLab (depending on the integrations used), approvals, audit events and workflow statuses.",
        },
        {
          h: "Technical and security data",
          p: "IP address, browser and user-agent data, log files, error messages, and audit and security events.",
        },
        {
          h: "Website data",
          p: "Pages visited and referrer. Aggregate, privacy-friendly analytics may be used if enabled at a later stage.",
        },
        {
          h: "Integration data",
          p: "Metadata and content required to make connected systems work. Tokens and secrets are processed securely and are not shown publicly.",
        },
        {
          h: "AI-related data",
          p: "Prompts, context and outputs required to execute workflows, only to the extent that customers or users provide these within the service.",
        },
      ],
      outro:
        "Depending on the customer configuration, we may also process codebase or repository metadata, to the extent necessary to deliver the service.",
    },
    s3: {
      title: "Why do we process data?",
      list: [
        "responding to contact requests;",
        "delivering the FixControl service;",
        "creating and managing accounts, tenants and projects;",
        "processing support and engineering workflows;",
        "displaying and executing approvals;",
        "running integrations with customer tools;",
        "security, audit logging and fraud prevention;",
        "improving the website and the product experience;",
        "complying with legal obligations.",
      ],
    },
    s4: {
      title: "Legal bases",
      intro: "We process personal data on the following GDPR legal bases:",
      list: [
        "the performance of a contract;",
        "a legitimate interest, for example security, audit logging and product improvement;",
        "compliance with a legal obligation;",
        "your consent where necessary, for example for non-essential cookies or certain marketing communications;",
        "pre-contractual communication for a demo or contact request.",
      ],
    },
    s5: {
      title: "AI processing",
      paras: [
        "FixControl uses AI features to support workflows, for example for triage, summaries, plan proposals, draft replies or remediation proposals. Human approval remains part of high-risk actions.",
        "Which AI services or models are used may depend on the customer configuration. Customer data is used only to deliver the service and is not sold.",
        "Depending on the configuration, AI service providers may be engaged as processors. Customer agreements may set out additional terms regarding which AI services are used and which data is shared with them.",
      ],
    },
    s6: {
      title: "Integrations and third parties",
      intro:
        "To deliver the service we may rely on processors and other third parties. Which parties these are depends on the customer configuration. Categories include, among others:",
      list: [
        "hosting and infrastructure providers;",
        "email and communication providers;",
        "an analytics provider, if enabled;",
        "AI service providers;",
        "integration platforms such as Slack, Microsoft Teams/Office 365, Google (Gmail), Freshdesk, Jira, Linear, GitHub, GitLab and Kubernetes/Argo, depending on the customer configuration;",
        "payment or administration providers, if relevant at a later stage.",
      ],
      subIntro:
        "Independent of the customer configuration, we rely on the following subprocessors by default for the core functionality of the service:",
      subList: [
        "TransIP B.V. (hosting, the Netherlands) — hosts the FixControl service and stored data on infrastructure in the European Union;",
        "Anthropic, PBC (AI language models) — processes prompts, context and outputs to the extent necessary to deliver the AI features of the service;",
        "Resend, Inc. (transactional email) — handles email sent from the service, such as notifications and approval requests.",
      ],
      subOutro:
        "We may update this list of subprocessors from time to time. The current list is always available in this privacy statement; in the event of significant changes, we may actively inform customers.",
      outro:
        "We share data only to the extent necessary to deliver and secure the service, to comply with legal obligations, or based on the customer configuration. Specific processors and arrangements may be recorded in a data processing agreement (DPA) or in the customer contract.",
    },
    s7: {
      title: "International transfers",
      paras: [
        "The FixControl service itself is hosted on infrastructure in the European Union (Netherlands).",
        "Some processors, including the AI and email service providers named above, may be located outside the European Economic Area (EEA). Where that is the case and necessary, appropriate safeguards are used, such as standard contractual clauses or comparable mechanisms.",
      ],
    },
    s8: {
      title: "Retention periods",
      intro:
        "We do not retain data longer than necessary. The retention period depends on the type of data and the purpose:",
      list: [
        "contact requests: for as long as needed for follow-up and business administration;",
        "account data: for as long as the account is active or as long as required for legal or contractual obligations;",
        "audit and security logs: for as long as needed for security, compliance and dispute handling;",
        "customer data: in accordance with the customer contract, the configuration or the data processing agreement;",
        "website analytics: aggregated and for as short a period as is practically appropriate.",
      ],
    },
    s9: {
      title: "Security",
      intro:
        "We take appropriate technical and organisational measures to protect personal data, including:",
      list: [
        "access control and roles;",
        "scope per tenant and project;",
        "logging and audit trails;",
        "encryption where appropriate;",
        "secure management of secrets;",
        "human approvals for high-risk workflows;",
        "monitoring and security measures.",
      ],
    },
    s10: {
      title: "Cookies and analytics",
      intro:
        "FixControl uses privacy-friendly, aggregated website analytics to understand which public pages are visited. We do not use session replay or advertising tracking on the public website unless explicitly stated.",
      list: [
        "necessary and functional cookies may be used to make the website and the service work;",
        "limited, privacy-friendly analytics may be used to measure the performance of the public website;",
        "tracking cookies, advertising pixels or session replay are not used, unless explicitly stated and where required with consent;",
        "you can manage cookies through your browser settings.",
      ],
    },
    s11: {
      title: "Your rights",
      intro: "Under the GDPR you have, among others, the following rights:",
      list: [
        "the right of access;",
        "the right to rectification;",
        "the right to erasure;",
        "the right to restriction of processing;",
        "the right to object;",
        "the right to data portability;",
        "the right to withdraw consent you have given;",
        "the right to lodge a complaint with the Dutch Data Protection Authority (Autoriteit Persoonsgegevens).",
      ],
      contactBefore: "Would you like to exercise one of these rights? Please contact us at ",
      contactAfter: ".",
    },
    s12: {
      title: "Contact",
      intro:
        "Do you have questions about this privacy statement or about the processing of your data? Please get in touch with us.",
      tradeNameLine: `a trade name of ${COMPANY.legalName}`,
      kvkLabel: "KvK",
      vatLabel: "VAT ID",
      emailLabel: "Email",
    },
    s13: {
      title: "Changes",
      paras: [
        "We may amend this privacy statement from time to time. The most recent version is always available on this page. In the event of significant changes, we may actively inform you.",
      ],
    },
    contactLink: "Contact us",
  },
} as const;

function Section({
  id,
  number,
  title,
  children,
}: {
  id: string;
  number: number;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-12 scroll-mt-24">
      <h2 className="flex items-baseline gap-3 text-[19px] md:text-[21px] font-semibold tracking-tight text-fg-bright">
        <span className="text-[13px] font-mono text-fg-muted">
          {String(number).padStart(2, "0")}
        </span>
        {title}
      </h2>
      <div className="mt-4 space-y-4 text-[14px] text-fg-dim leading-relaxed">
        {children}
      </div>
    </section>
  );
}

export function PrivacyArticle() {
  const { locale } = useLocale();
  const c = locale === "nl" ? COPY.nl : COPY.en;
  const { address } = COMPANY;

  return (
    <div className="min-h-screen bg-bg-base text-fg">
      <LandingNav />
      <main className="relative">
        <div
          aria-hidden
          className="pointer-events-none absolute inset-x-0 -top-40 h-[500px] bg-[radial-gradient(ellipse_at_top,rgba(244,63,142,0.15),transparent_60%)]"
        />
        <div className="relative mx-auto max-w-3xl px-6 pt-16 pb-24">
          {/* Header */}
          <span className="inline-flex items-center gap-2 rounded-full border border-brand-500/25 bg-brand-500/5 px-3 py-1 text-[11px] font-medium uppercase tracking-[0.18em] text-brand-300">
            <span className="h-1.5 w-1.5 rounded-full bg-brand-300" />
            {c.eyebrow}
          </span>
          <h1 className="mt-5 text-balance text-4xl md:text-5xl font-semibold tracking-tight text-fg-bright leading-[1.1]">
            {c.title}
          </h1>
          <p className="mt-5 text-[15px] text-fg-dim leading-relaxed">
            {c.intro}
          </p>
          <p className="mt-4 text-[12px] font-mono uppercase tracking-[0.18em] text-fg-muted">
            {c.lastUpdated}: {LAST_UPDATED}
          </p>

          <Section id="verantwoordelijke" number={1} title={c.s1.title}>
            <p>{c.s1.p1}</p>
            <p>{c.s1.p2}</p>
          </Section>

          <Section id="gegevens" number={2} title={c.s2.title}>
            <p>{c.s2.intro}</p>
            {c.s2.cats.map((cat) => (
              <div key={cat.h}>
                <h3 className="text-[14px] font-medium text-fg">{cat.h}</h3>
                <p>{cat.p}</p>
              </div>
            ))}
            <p>{c.s2.outro}</p>
          </Section>

          <Section id="doeleinden" number={3} title={c.s3.title}>
            <ul className="list-disc space-y-1.5 pl-5">
              {c.s3.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Section>

          <Section id="grondslagen" number={4} title={c.s4.title}>
            <p>{c.s4.intro}</p>
            <ul className="list-disc space-y-1.5 pl-5">
              {c.s4.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Section>

          <Section id="ai" number={5} title={c.s5.title}>
            {c.s5.paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section id="derden" number={6} title={c.s6.title}>
            <p>{c.s6.intro}</p>
            <ul className="list-disc space-y-1.5 pl-5">
              {c.s6.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p>{c.s6.subIntro}</p>
            <ul className="list-disc space-y-1.5 pl-5">
              {c.s6.subList.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p>{c.s6.subOutro}</p>
            <p>{c.s6.outro}</p>
          </Section>

          <Section id="doorgifte" number={7} title={c.s7.title}>
            {c.s7.paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <Section id="bewaartermijnen" number={8} title={c.s8.title}>
            <p>{c.s8.intro}</p>
            <ul className="list-disc space-y-1.5 pl-5">
              {c.s8.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Section>

          <Section id="beveiliging" number={9} title={c.s9.title}>
            <p>{c.s9.intro}</p>
            <ul className="list-disc space-y-1.5 pl-5">
              {c.s9.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Section>

          <Section id="cookies" number={10} title={c.s10.title}>
            <p>{c.s10.intro}</p>
            <ul className="list-disc space-y-1.5 pl-5">
              {c.s10.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </Section>

          <Section id="rechten" number={11} title={c.s11.title}>
            <p>{c.s11.intro}</p>
            <ul className="list-disc space-y-1.5 pl-5">
              {c.s11.list.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <p>
              {c.s11.contactBefore}
              <a
                href={`mailto:${COMPANY.publicEmail}`}
                className="text-brand-300 underline underline-offset-2 hover:text-brand-200"
              >
                {COMPANY.publicEmail}
              </a>
              {c.s11.contactAfter}
            </p>
          </Section>

          <Section id="contact" number={12} title={c.s12.title}>
            <p>{c.s12.intro}</p>
            <address className="mt-2 space-y-1 not-italic">
              <div className="text-fg">FixControl</div>
              <div>{c.s12.tradeNameLine}</div>
              <div>{address.street}</div>
              <div>
                {address.postalCode} {address.city}
              </div>
              <div>{address.country}</div>
              {COMPANY.chamberOfCommerceNumber ? (
                <div>{c.s12.kvkLabel}: {COMPANY.chamberOfCommerceNumber}</div>
              ) : null}
              {COMPANY.vatId ? (
                <div>{c.s12.vatLabel}: {COMPANY.vatId}</div>
              ) : null}
              <div>
                {c.s12.emailLabel}:{" "}
                <a
                  href={`mailto:${COMPANY.publicEmail}`}
                  className="text-brand-300 hover:text-brand-200"
                >
                  {COMPANY.publicEmail}
                </a>
              </div>
            </address>
          </Section>

          <Section id="wijzigingen" number={13} title={c.s13.title}>
            {c.s13.paras.map((p) => (
              <p key={p}>{p}</p>
            ))}
          </Section>

          <div className="mt-14 border-t border-line-subtle pt-6 text-[13px] text-fg-muted">
            <Link href="/contact" className="text-brand-300 hover:text-brand-200">
              {c.contactLink}
            </Link>
          </div>
        </div>
      </main>
    </div>
  );
}
